import React, { useEffect, useState } from "react";
import { Bell, BellOff, BellRing, CheckCircle2, Loader2, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  enablePushNotifications,
  disablePushNotifications,
  getCurrentSubscription,
  permissionState,
  sendTestPush,
} from "@/lib/pushNotifications";

const LS_DISMISSED_KEY = "hr_push_banner_dismissed";

/**
 * Two surfaces:
 *  - <PushBanner /> — small top-of-dashboard prompt that asks the user to
 *    enable notifications once. Hides after one click (enable OR dismiss).
 *  - <PushSettingsCard /> — long-form card on My Profile to toggle.
 */

export function PushBanner() {
  const [state, setState] = useState("idle"); // idle|enabling|done|hidden
  const [hidden, setHidden] = useState(true);

  useEffect(() => {
    (async () => {
      const perm = permissionState();
      const dismissed = window.localStorage.getItem(LS_DISMISSED_KEY) === "1";
      const sub = await getCurrentSubscription();
      // Show only when permission is default and not already subscribed and not dismissed
      const show = perm === "default" && !sub && !dismissed;
      setHidden(!show);
    })();
  }, []);

  const enable = async () => {
    setState("enabling");
    const res = await enablePushNotifications();
    if (res.ok) {
      toast.success("Browser notifications enabled — you'll get instant alerts.");
      setState("done");
      setHidden(true);
    } else {
      toast.error(res.reason || "Could not enable notifications");
      setState("idle");
    }
  };

  const dismiss = () => {
    window.localStorage.setItem(LS_DISMISSED_KEY, "1");
    setHidden(true);
  };

  if (hidden) return null;
  return (
    <div className="bg-gradient-to-r from-indigo-50 to-violet-50 border border-indigo-200 rounded-md p-4 flex items-center gap-4 flex-wrap" data-testid="push-banner">
      <div className="w-10 h-10 rounded-full bg-indigo-600 text-white flex items-center justify-center shrink-0">
        <BellRing className="w-5 h-5" />
      </div>
      <div className="flex-1 min-w-[220px]">
        <div className="font-display text-lg font-bold tracking-tight">Get instant alerts</div>
        <p className="text-xs text-zinc-600">
          One click. We'll ping you when you get a new task, warning, payslip or anything else important — even when this tab is closed.
        </p>
      </div>
      <div className="flex gap-2 shrink-0">
        <Button variant="ghost" onClick={dismiss} data-testid="push-banner-dismiss">Not now</Button>
        <Button
          onClick={enable}
          disabled={state === "enabling"}
          className="bg-indigo-600 hover:bg-indigo-700"
          data-testid="push-banner-enable"
        >
          {state === "enabling" ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Bell className="w-4 h-4 mr-1.5" />}
          Enable browser notifications
        </Button>
      </div>
    </div>
  );
}

export function PushSettingsCard() {
  const [perm, setPerm] = useState("default");
  const [subscribed, setSubscribed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [testing, setTesting] = useState(false);

  const refresh = async () => {
    setPerm(permissionState());
    const s = await getCurrentSubscription();
    setSubscribed(!!s);
  };
  useEffect(() => { refresh(); }, []);

  const enable = async () => {
    setBusy(true);
    const r = await enablePushNotifications();
    if (r.ok) toast.success("Browser notifications enabled");
    else toast.error(r.reason || "Could not enable notifications");
    await refresh();
    setBusy(false);
  };

  const disable = async () => {
    setBusy(true);
    await disablePushNotifications();
    toast.success("Browser notifications turned off");
    await refresh();
    setBusy(false);
  };

  const test = async () => {
    setTesting(true);
    try {
      const { data } = await sendTestPush();
      if (data?.delivered > 0) {
        toast.success(`Test push sent (${data.delivered} device${data.delivered === 1 ? "" : "s"}). Check your notification tray.`);
      } else {
        toast.error("No devices to push to — make sure notifications are enabled first.");
      }
    } catch (e) {
      toast.error("Test push failed");
    } finally {
      setTesting(false);
    }
  };

  const blocked = perm === "denied";
  const unsupported = perm === "unsupported";

  return (
    <div className="bg-white border border-zinc-200 rounded-md p-6" data-testid="push-settings-card">
      <div className="flex items-start gap-3 mb-4">
        <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${subscribed ? "bg-emerald-100 text-emerald-700" : "bg-zinc-100 text-zinc-600"}`}>
          {subscribed ? <CheckCircle2 className="w-5 h-5" /> : <Bell className="w-5 h-5" />}
        </div>
        <div className="flex-1">
          <h3 className="font-display text-xl font-bold tracking-tight">Browser notifications</h3>
          <p className="text-xs text-zinc-500 mt-1">
            Get instant pop-up alerts when something happens — new task, warning, appreciation, payslip, leave decision, mood reminder. Works even when this tab is closed.
          </p>
        </div>
      </div>

      {unsupported && (
        <div className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded p-3 inline-flex items-center gap-2">
          <AlertCircle className="w-4 h-4" />
          This browser doesn't support push notifications. Try Chrome, Edge or Firefox on desktop, or Chrome/Safari on mobile.
        </div>
      )}
      {blocked && (
        <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-3 inline-flex items-center gap-2">
          <BellOff className="w-4 h-4" />
          Notifications are blocked in your browser settings. Click the lock icon in the address bar → Notifications → Allow, then refresh.
        </div>
      )}
      {!unsupported && !blocked && (
        <div className="flex gap-2 flex-wrap">
          {!subscribed ? (
            <Button onClick={enable} disabled={busy} className="bg-indigo-600 hover:bg-indigo-700" data-testid="push-enable-btn">
              {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Bell className="w-4 h-4 mr-1.5" />}
              Enable browser notifications
            </Button>
          ) : (
            <>
              <Button onClick={test} disabled={testing} variant="outline" data-testid="push-test-btn">
                {testing ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <BellRing className="w-4 h-4 mr-1.5" />}
                Send test notification
              </Button>
              <Button onClick={disable} disabled={busy} variant="outline" className="text-zinc-700" data-testid="push-disable-btn">
                {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <BellOff className="w-4 h-4 mr-1.5" />}
                Turn off
              </Button>
              <div className="text-xs text-emerald-700 self-center font-medium" data-testid="push-state-on">
                ● You're subscribed
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
